import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { useNavigate } from 'react-router-dom';
import socket from '../services/socket';
import { API_BASE_URL } from '../config';
import generateAvatar from '../utils/generateAvatar';
import '../styles/Leaderboard.css';

const Leaderboard = ({ userId }) => {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [prevPositions, setPrevPositions] = useState({}); // Track previous positions for animation
  const [positionChanges, setPositionChanges] = useState({}); // Persist position changes for visibility
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('totalValue'); // 'totalValue' or 'percentGain'
  const [flashingUsers, setFlashingUsers] = useState(new Set()); // Track users with value changes
  const prevLeadersRef = useRef([]);
  const positionChangeTimeoutRef = useRef({});
  const [showPicture, setShowPicture] = useState(null);

  // Helper: get profile picture URL for a trader
  const getProfilePicture = (trader) => {
    if (trader.hasPicture) {
      return `${API_BASE_URL}/api/profile/picture/${trader.id}`;
    }
    return generateAvatar(trader.username || trader.displayName);
  };

  // Fetch leaderboard via HTTP as primary method
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/leaderboard`);
        if (!response.ok) throw new Error('Failed to fetch leaderboard');
        const data = await response.json();

        // Calculate position changes from previous data
        if (prevLeadersRef.current.length > 0) {
          const oldPositions = {};
          const oldValues = {};

          prevLeadersRef.current.forEach((leader, index) => {
            oldPositions[leader.id] = index;
            oldValues[leader.id] = leader.totalValue;
          });

          // Find users whose values changed
          const changedUsers = new Set();
          const newChanges = {};

          data.forEach((leader, newIndex) => {
            const oldValue = oldValues[leader.id];
            if (oldValue !== undefined && oldValue !== leader.totalValue) {
              changedUsers.add(leader.id);
            }

            // Calculate position change
            const oldPosition = oldPositions[leader.id];
            if (oldPosition !== undefined && oldPosition !== newIndex) {
              const change = oldPosition - newIndex; // Positive = moved up
              newChanges[leader.id] = change;

              // Clear any existing timeout for this user
              if (positionChangeTimeoutRef.current[leader.id]) {
                clearTimeout(positionChangeTimeoutRef.current[leader.id]);
              }

              // Set timeout to clear position change after 5 seconds
              positionChangeTimeoutRef.current[leader.id] = setTimeout(() => {
                setPositionChanges(prev => {
                  const updated = { ...prev };
                  delete updated[leader.id];
                  return updated;
                });
              }, 5000);
            }
          });

          // Update position changes (merge with existing)
          if (Object.keys(newChanges).length > 0) {
            setPositionChanges(prev => ({ ...prev, ...newChanges }));
          }

          // Flash changed users
          if (changedUsers.size > 0) {
            setFlashingUsers(changedUsers);
            setTimeout(() => setFlashingUsers(new Set()), 1000);
          }

          setPrevPositions(oldPositions);
        }

        prevLeadersRef.current = data;
        setLeaders(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        setLoading(false);
      }
    };

    fetchLeaderboard();
    // Refresh every 10 seconds
    const interval = setInterval(fetchLeaderboard, 10000);
    return () => clearInterval(interval);
  }, []);

  // Listen for real-time leaderboard updates via socket
  useEffect(() => {
    const handleLeaderboardUpdate = (data) => {
      const newLeaders = data.leaderboard || [];

      // Calculate position changes and value changes
      if (prevLeadersRef.current.length > 0) {
        const oldPositions = {};
        const oldValues = {};

        prevLeadersRef.current.forEach((leader, index) => {
          oldPositions[leader.id] = index;
          oldValues[leader.id] = leader.totalValue;
        });

        // Find users whose values changed
        const changedUsers = new Set();
        const newChanges = {};

        newLeaders.forEach((leader, newIndex) => {
          const oldValue = oldValues[leader.id];
          if (oldValue !== undefined && oldValue !== leader.totalValue) {
            changedUsers.add(leader.id);
          }

          // Calculate position change
          const oldPosition = oldPositions[leader.id];
          if (oldPosition !== undefined && oldPosition !== newIndex) {
            const change = oldPosition - newIndex;
            newChanges[leader.id] = change;

            if (positionChangeTimeoutRef.current[leader.id]) {
              clearTimeout(positionChangeTimeoutRef.current[leader.id]);
            }

            positionChangeTimeoutRef.current[leader.id] = setTimeout(() => {
              setPositionChanges(prev => {
                const updated = { ...prev };
                delete updated[leader.id];
                return updated;
              });
            }, 5000);
          }
        });

        if (Object.keys(newChanges).length > 0) {
          setPositionChanges(prev => ({ ...prev, ...newChanges }));
        }

        // Flash changed users
        if (changedUsers.size > 0) {
          setFlashingUsers(changedUsers);
          setTimeout(() => setFlashingUsers(new Set()), 1000);
        }

        setPrevPositions(oldPositions);
      }

      prevLeadersRef.current = newLeaders;
      setLeaders(newLeaders);
      setLoading(false);
    };

    // Subscribe to socket events
    socket.on('leaderboard-update', handleLeaderboardUpdate);

    // Request initial leaderboard data
    socket.emit('get-leaderboard', { userId });

    return () => {
      socket.off('leaderboard-update', handleLeaderboardUpdate);
    };
  }, [userId]);

  // Navigate to a trader's profile
  const viewTraderProfile = (traderId) => {
    navigate(`/portfolio/${traderId}`);
  };

  // Get sorted leaderboard with position tracking
  const sortedLeaders = React.useMemo(() => {
    let sorted;
    if (sortBy === 'totalValue') {
      sorted = [...leaders].sort((a, b) => b.totalValue - a.totalValue);
    } else {
      sorted = [...leaders].sort((a, b) => b.percentGain - a.percentGain);
    }

    // Add position data for animation - use persistent positionChanges
    return sorted.map((leader, index) => {
      return {
        ...leader,
        currentPosition: index,
        positionChange: positionChanges[leader.id] || 0
      };
    });
  }, [leaders, sortBy, positionChanges]);

  // Get position change indicator
  const getPositionIndicator = (change) => {
    if (change > 0) {
      return <span className="position-change up">↑ {change}</span>;
    } else if (change < 0) {
      return <span className="position-change down">↓ {Math.abs(change)}</span>;
    }
    return null;
  };

  // Get rank badge for top 3
  const getRankBadge = (position) => {
    if (position === 0) return <span className="rank-badge gold">🥇</span>;
    if (position === 1) return <span className="rank-badge silver">🥈</span>;
    if (position === 2) return <span className="rank-badge bronze">🥉</span>;
    return null;
  };

  // Render loading state
  if (loading) {
    return (
      <div className="leaderboard-loading">
        <div className="spinner"></div>
        <p>LOADING RANKINGS...</p>
      </div>
    );
  }

  const isMobile = window.innerWidth <= 480;

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <h1>{isMobile ? 'RANKINGS' : 'RANKINGS'}</h1>
        <p className="leaderboard-subtitle">
          {sortedLeaders.length} Traders
        </p>

        <div className="sort-controls">
          <button
            className={`sort-btn ${sortBy === 'totalValue' ? 'active' : ''}`}
            onClick={() => setSortBy('totalValue')}
          >
            {isMobile ? '[VALUE]' : '[PORTFOLIO VALUE]'}
          </button>
          <button
            className={`sort-btn ${sortBy === 'percentGain' ? 'active' : ''}`}
            onClick={() => setSortBy('percentGain')}
          >
            {isMobile ? '[% GAIN]' : '[PERCENT GAIN]'}
          </button>
        </div>
      </div>

      {/* Current user rank */}
      {userId && sortedLeaders.length > 0 && (() => {
        const userIndex = sortedLeaders.findIndex(t => t.id === userId);
        if (userIndex === -1) return null;
        const userData = sortedLeaders[userIndex];
        return (
          <div className="user-rank-summary">
            <div className="rank-summary-content">
              <div className="rank-summary-position">
                RANK: <span className="rank-highlight">#{userIndex + 1}</span> / {sortedLeaders.length}
              </div>
              <div className="rank-summary-stats">
                <span>VAL: {formatCurrency(userData.totalValue)}</span>
                <span className={userData.percentGain >= 0 ? 'positive' : 'negative'}>
                  PNL: {userData.percentGain >= 0 ? '+' : ''}{formatPercent(userData.percentGain)}
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Leaderboard table */}
      <div className="leaderboard-table">
        <div className="leaderboard-table-header">
          <div className="rank-column">Rank</div>
          <div className="trader-column">Trader</div>
          <div className="balance-column">Cash Balance</div>
          <div className="value-column">Portfolio Value</div>
          <div className="pnl-column">P&L</div>
        </div>

        <div className="leaderboard-rows">
          {sortedLeaders.map((trader) => {
            const isCurrentUser = trader.id === userId;
            const isFlashing = flashingUsers.has(trader.id);
            const isTop3 = trader.currentPosition < 3;

            return (
              <div
                key={trader.id}
                className={`
                  leaderboard-row
                  ${isCurrentUser ? 'current-user' : ''}
                  ${isTop3 ? 'top-three' : ''}
                  ${isFlashing ? 'flashing' : ''}
                  position-${trader.currentPosition}
                `}
                onClick={() => viewTraderProfile(trader.id)}
                style={{
                  '--position': trader.currentPosition,
                  '--old-position': prevPositions[trader.id] ?? trader.currentPosition
                }}
              >
                {/* Rank Column */}
                <div className="rank-column">
                  <div className="rank-number">
                    {trader.currentPosition + 1}
                  </div>
                  {getRankBadge(trader.currentPosition)}
                  {trader.positionChange !== 0 && getPositionIndicator(trader.positionChange)}
                </div>

                {/* Trader Column */}
                <div className="trader-column">
                  <img
                    src={getProfilePicture(trader)}
                    alt={trader.username || trader.displayName}
                    className="trader-avatar clickable-avatar"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPicture(getProfilePicture(trader));
                    }}
                  />
                  <div className="trader-info">
                    <div className="trader-name">
                      {isCurrentUser && <span className="you-badge">YOU</span>}
                      {trader.username || trader.displayName}
                    </div>
                    <div className="trader-id">#{trader.shortId}</div>
                  </div>
                </div>

                {/* Balance Column */}
                <div className="balance-column">
                  <div className="balance-value">
                    {formatCurrency(trader.balance)}
                  </div>
                  <div className="balance-label">Cash</div>
                </div>

                {/* Total Value Column */}
                <div className="value-column">
                  <div className="total-value">
                    {formatCurrency(trader.totalValue)}
                  </div>
                  <div className="positions-value">
                    {formatCurrency(trader.positionsValue)} in positions
                  </div>
                </div>

                {/* P&L Column */}
                <div className={`pnl-column ${trader.percentGain >= 0 ? 'positive' : 'negative'}`}>
                  <div className="pnl-percent">
                    {trader.percentGain >= 0 ? '+' : ''}
                    {formatPercent(trader.percentGain)}
                  </div>
                  <div className="pnl-amount">
                    {trader.percentGain >= 0 ? '+' : ''}
                    {formatCurrency(trader.totalValue - 10000)}
                  </div>
                </div>
              </div>
            );
          })}

          {sortedLeaders.length === 0 && (
            <div className="no-data">
              <p>NO TRADERS REGISTERED // AWAITING PARTICIPANTS</p>
            </div>
          )}
        </div>
      </div>

      {/* Profile Picture Lightbox */}
      {showPicture && (
        <div className="picture-lightbox" onClick={() => setShowPicture(null)}>
          <img src={showPicture} alt="Profile" />
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
